import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { customerQueries } from '@as-react/common-queries';
import { useQuery } from '@tanstack/react-query';
import { TextField, Grid, SelectAlpha } from '@yonderland/andes-react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { mapWithAssert } from '../../../util/ArrayUtil';
import { generateFieldName } from '../../form/address-fields/addressFields.utils';
import { FormField } from '../../form/FormField';
import { CustomerDetailsFieldName } from '../../form/profile-fields/CustomerDetailsFields.utils';
export const CustomerDetailsFields = ({ prefix }) => {
    const { t } = useTranslation([I18N_NAMESPACES.checkout, I18N_NAMESPACES.general]);
    const { defaultRequestParams, servicesEndpoint, lang } = useAemConfig();
    const [idTitle, firstName, lastName, middleName] = mapWithAssert([
        CustomerDetailsFieldName.ID_TITLE,
        CustomerDetailsFieldName.FIRST_NAME,
        CustomerDetailsFieldName.LAST_NAME,
        CustomerDetailsFieldName.MIDDLE_NAME,
    ], name => generateFieldName(name, prefix));
    const { getTitles } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const { data: customerTitles } = useQuery(getTitles({ lang }));
    const titleOptions = (customerTitles === null || customerTitles === void 0 ? void 0 : customerTitles.options.map(title => ({ id: title.value, text: title.label }))) || [];
    const showMiddleName = defaultRequestParams.market === 'nl';
    const fieldRowOffset = showMiddleName ? { ['@xl']: 2, ['@2xl']: 1 } : { ['@xl']: 1 };
    return (_jsxs(Grid, { columns: 12, gapY: 6, fullWidth: true, isQueryContainer: true, children: [_jsx(Grid.Cell, { span: {
                    base: 8,
                    ['@sm']: 6,
                    ['@xl']: showMiddleName ? 4 : 3,
                    ['@2xl']: 3,
                }, rowOffset: 1, children: _jsx(FormField, { name: idTitle, required: true, children: (_a) => {
                        var _b = _a.fieldProps, { ref, onChange, value, disabled } = _b, options = __rest(_b, ["ref", "onChange", "value", "disabled"]), { error, controls } = _a;
                        return (_jsx(SelectAlpha, Object.assign({}, options, { input: {
                                id: idTitle,
                                label: t('checkout:checkout.customer.title.label'),
                                placeholder: t('checkout:checkout.customer.title.placeholder'),
                                required: true,
                                disabled,
                                error,
                            }, items: titleOptions, onSelect: value => {
                                onChange(value.id);
                                controls.validate();
                            }, value: titleOptions.length ? value : undefined, testId: "customer-title" })));
                    } }) }), _jsx(Grid.Cell, { span: { base: 12, ['@xl']: 4, ['@2xl']: showMiddleName ? 3 : 4 }, rowOffset: fieldRowOffset, children: _jsx(FormField, { name: firstName, required: true, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, options = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(TextField, Object.assign({}, options, { error: error, id: firstName, maxLength: 50, required: true, label: t('checkout:checkout.customer.firstname.label'), testId: "customer-firstname" })));
                    } }) }), showMiddleName && (_jsx(Grid.Cell, { span: { base: 12, ['@xl']: 3, ['@2xl']: 2 }, rowOffset: fieldRowOffset, children: _jsx(FormField, { name: middleName, required: true, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, options = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(TextField, Object.assign({}, options, { error: error, id: middleName, maxLength: 50, optionalText: t('checkout:checkout.customer.optional'), label: t('checkout:checkout.customer.middlename.label'), testId: "customer-middlename" })));
                    } }) })), _jsx(Grid.Cell, { span: {
                    base: 12,
                    ['@xl']: 5,
                    ['@2xl']: showMiddleName ? 4 : 6,
                }, rowOffset: fieldRowOffset, children: _jsx(FormField, { name: lastName, required: true, children: (_a) => {
                        var _b = _a.fieldProps, { ref } = _b, options = __rest(_b, ["ref"]), { error } = _a;
                        return (_jsx(TextField, Object.assign({}, options, { error: error, id: lastName, maxLength: 50, required: true, label: t('checkout:checkout.customer.lastname.label'), testId: "customer-lastname" })));
                    } }) })] }));
};
